"use client";

import {
    createEvent, CreateEventData,
    deleteEvent, DeleteEventData,
    getEvent, GetEventData,
    updateEvent, UpdateEventData,
    GetTranscriptionData,
    GenerateEventTranscriptionSummaryData,
} from "@/api";
import { calendar_event } from "@/app/typing/events";
import { useLocalNumberList } from "@/components/path-params";
import { EntryItem, TextInputType, useRefState, ActionHandler, DateTimeRangeValue } from "@/components/UI/tools";
import { useEffect, useState, ReactNode } from "react";
import { appState } from "@/app/appState";
import { chatState } from "@/app/chatState";
import { observer } from "mobx-react-lite";
import { Request } from "@/components/CRUD";
import { PageBox } from "@/components/UI/page";
import { dispersedMatch } from "@/components/searchUtils";
import { Org, User } from "@/components/UI/svgs";
import { shortenLabel } from "../chat/chatObjectEditorWindow/chatObjectEditorWindow";
import { getTranscription, generateEventTranscriptionSummary } from "@/api/sdk.gen";

const EventEntry = observer(({ id, setId }: { id?: number; setId: (id?: number) => void }) => {
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);
    const [event, setEvent] = useState<calendar_event | undefined>(undefined);
    const [deleting, setDeleting] = useState(false);
    const [lock, setLock] = useRefState(false);
    const [edit, setEdit] = useState(false);

    const [ids, setIds] = useLocalNumberList("event_ids");

    const [title, setTitle] = useRefState<string>("");
    const [description, setDescription] = useRefState<string | undefined>(undefined);
    const [location, setLocation] = useRefState<string | undefined>(undefined);
    const [startTime, setStartTime] = useRefState<string>("");
    const [endTime, setEndTime] = useRefState<string>("");
    const [allDay, setAllDay] = useRefState<boolean>(false);
    const [timezone, setTimezone] = useRefState<string>("UTC");
    const [tagId, setTagId] = useRefState<number | undefined>(undefined);

    const [tagSearch, setTagSearch] = useState<string>("");

    useEffect(() => {
        appState.getTags({ pageNumber: 0, search: tagSearch });
    },
        [tagSearch]);

    const createEventReq = Request<CreateEventData, any>(
        createEvent,
        () => ({
            body: {
                tag_id: tagId.current!,
                title: title.current,
                description: description.current ?? null,
                location: location.current ?? null,
                start_time: startTime.current,
                end_time: endTime.current,
                all_day: allDay.current,
                timezone: timezone.current,
            },
        }),
        setLoading,
        (data: any) => {
            setId(data?.event_id);
            appState.getEvents({ pageNumber: 0, search: "" });
        },
        (error) => {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorCreateEvent"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            });
        },
        () => title.current.length > 0 && tagId.current !== undefined && startTime.current.length > 0 && endTime.current.length > 0,
    );

    const deleteEventReq = Request<DeleteEventData, any>(
        deleteEvent,
        () => ({ body: { id: id! } }),
        setDeleting,
        () => { setId(undefined); appState.getEvents({ pageNumber: 0, search: "" }); },
        (error) => {
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorDeleteEvent") });
        },
        () => id !== undefined && id > 0,
    );

    const updateEventReq = Request<UpdateEventData, any>(
        updateEvent,
        () => ({
            body: {
                id: id!,
                title: title.current,
                description: description.current ?? null,
                location: location.current ?? null,
                start_time: startTime.current,
                end_time: endTime.current,
                all_day: allDay.current,
                timezone: timezone.current,
            },
        }),
        setLoading,
        () => { appState.getEvents({ pageNumber: 0, search: "" }); },
        (error) => {
            setRefresh((p) => p + 1);
            setEdit(false);
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorUpdateEvent"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            });
        },
        () => id !== undefined && id > 0 && title.current.length > 0 && startTime.current.length > 0 && endTime.current.length > 0,
    );

    const getEventReq = Request<GetEventData, any>(
        getEvent,
        () => ({ body: { id: id! } }),
        setLoading,
        (data: any) => { setEvent(data.event); },
        (error) => {
            setEvent(undefined);
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorGetEvent") });
        },
        () => id !== undefined && id > 0,
    );

    useEffect(() => {
        setEvent(undefined);
        if (id && id > 0) getEventReq();
    }, [id]);

    const refreshRefs = () => {
        if (event) {
            setLock(event.user_lock ? !(event.user_lock === appState.user?.id) : false);
            setTitle(event.title);
            setDescription(event.description ?? undefined);
            setLocation(event.location ?? undefined);
            setStartTime(event.start_time);
            setEndTime(event.end_time);
            setAllDay(event.all_day);
            setTimezone(event.timezone);
            setTagId(event.tag_id);
        } else {
            setTitle("");
            setDescription(undefined);
            setLocation(undefined);
            setStartTime("");
            setEndTime("");
            setAllDay(false);
            setTimezone("UTC");
            setTagId(undefined);
        }
    };

    useEffect(() => {
        refreshRefs();
        setRefresh((prev) => prev + 1);
    }, [event]);

    // Format datetime for display
    const formatDateTime = (iso: string | undefined) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    };

    const fetchTranscription = Request<GetTranscriptionData, any>(
        getTranscription,
        () => ({ body: { event_id: id! } }),
        setLoading,
        (data: any) => {
            appState.setModal({
                title: appState.t("objects.transcriptionTitle"),
                body: <div className="p-4 whitespace-pre-wrap">{data.transcription || appState.t("objects.noTranscription")}</div>,
            });
        },
        (error) => {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorFetchTranscription"),
                comment: { name: appState.t("common.detail"), val: error?.data?.detail || error.message || "Unknown error" }
            });
        },
        () => id !== undefined && id > 0
    );

    const generateSummaryReq = Request<GenerateEventTranscriptionSummaryData, any>(
        generateEventTranscriptionSummary,
        () => ({ body: { event_id: id!, conversation_id: chatState.conversationId ?? undefined } }),
        setLoading,
        (_data: any) => {
            // complete=true: short save flagged unsummarised (no LLM).
            // Nothing is running in the background.
            if (_data?.complete) {
                return;
            }
            appState.setModal({
                title: appState.t("objects.summaryStartedTitle"),
                body: appState.t("objects.summaryStartedBody"),
            });
        },
        (error) => {
            const detail = error?.detail ?? error?.data?.detail;
            if (detail === "event_summarisation_in_progress") {
                appState.setModal({
                    title: appState.t("objects.summaryStartedTitle"),
                    body: appState.t("objects.summaryStartedBody"),
                });
                return;
            }
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorStartSummary"),
                comment: { name: appState.t("common.detail"), val: error?.data?.detail || error.message || "Unknown error" }
            });
        },
        () => id !== undefined && id > 0
    );

    return (
        <PageBox>
            <div className="h-full w-full relative">
                {(event && event.id === id) || id === -1 ? (
                    <>
                        {id && id !== -1 && (
                            <div className="absolute top-1 right-2 z-50 flex flex-row gap-1">
                                <ActionHandler funq={generateSummaryReq} disabled={loading} title={appState.t("objects.generateSummary")}>
                                    S
                                </ActionHandler>
                                <ActionHandler funq={fetchTranscription} disabled={loading} title={appState.t("objects.viewTranscription")}>
                                    T
                                </ActionHandler>
                            </div>
                        )}
                        <EntryItem
                            lock={lock.current}
                            setEdit={(val: boolean) => setEdit(val)}
                            edit={edit}
                            name={appState.t("objects.list.event")}
                            loading={loading}
                            deleting={deleting}
                            readonly={false}
                            createEntry={createEventReq}
                            deleteEntry={deleteEventReq}
                            updateEntry={updateEventReq}
                            refreshCount={refresh}
                            idParams={{
                                singular: { get: id, set: setId },
                                plural: { get: ids, set: setIds },
                            }}
                            refColumns={
                                [
                                    {
                                        type: "text",
                                        label: appState.t("objects.description"),
                                        value: description.current ?? "",
                                        onChange: setDescription,
                                        validator: () => true,
                                    },
                                    {
                                        type: "text",
                                        label: appState.t("objects.location"),
                                        value: location.current ?? "",
                                        onChange: setLocation,
                                        validator: () => true,
                                    },
                                ] as TextInputType[]
                            }
                            nameColumn={{
                                type: "text",
                                value: title.current,
                                onChange: setTitle,
                                validator: (val: string) => val.length > 0,
                                label: appState.t("objects.title"),
                            }}
                            stateColumns={[
                                {
                                    label: appState.t("objects.when"),
                                    type: "datetimerange" as const,
                                    onChange: (val: DateTimeRangeValue) => {
                                        setStartTime(val.startTime);
                                        setEndTime(val.endTime);
                                        setRefresh((p) => p + 1);
                                    },
                                    value: { startTime: startTime.current, endTime: endTime.current },
                                    validator: (val: DateTimeRangeValue) => val.startTime.length > 0 && val.endTime.length > 0,
                                    allDay: allDay.current,
                                    onAllDayChange: (val: boolean) => {
                                        setAllDay(val);
                                        setRefresh((p) => p + 1);
                                    },
                                },
                                id === -1
                                    ? {
                                        label: appState.t("objects.tag"),
                                        type: "single",
                                        onChange: (val) => setTagId(val),
                                        value: tagId.current,
                                        items: appState.tags
                                            .filter((x) => [4, 5, 6, 7].includes(x.effective_permission_type))
                                            .filter((x) => x.has_calendar)
                                            .map((x) => ({
                                                id: x.id,
                                                name: x.name,
                                            })),
                                        validator: (val: number | undefined) => val !== undefined,
                                    }
                                    : undefined,
                            ]}
                        />
                    </>
                ) : null}
            </div>
        </PageBox>
    );
});

export default EventEntry;
